import React, { useState } from 'react';
import { BiEdit, BiSave, BiX, BiText } from 'react-icons/bi';

const HtmlContentRenderer = ({ htmlContent, onContentChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editMode, setEditMode] = useState('textOnly'); // 'textOnly' or 'fullHtml'
  const [editedContent, setEditedContent] = useState(htmlContent || '');
  const [textOnlyContent, setTextOnlyContent] = useState('');

  // Extract text content from HTML
  const extractTextFromHtml = (html) => {
    if (!html) return '';
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Replace text content in HTML while preserving structure
  const replaceTextInHtml = (originalHtml, newText) => {
    if (!originalHtml) return newText;
    if (!newText) return originalHtml;
    
    try {
      // Create a temporary DOM element to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = originalHtml;
      
      // Get all text nodes
      const walker = document.createTreeWalker(
        tempDiv,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: function(node) {
            // Accept text nodes that are not empty or contain meaningful content
            return NodeFilter.FILTER_ACCEPT;
          }
        },
        false
      );
      
      const textNodes = [];
      let node;
      while (node = walker.nextNode()) {
        if (node.textContent && node.textContent.trim()) {
          textNodes.push(node);
        }
      }
      
      // If no text nodes found, wrap new text in a paragraph
      if (textNodes.length === 0) {
        tempDiv.innerHTML = `<p>${newText.replace(/\n/g, '<br>')}</p>`;
        return tempDiv.innerHTML;
      }
      
      // Split new text by double newlines (paragraphs) or single newlines
      const newTextParts = newText.split(/\n\s*\n/).filter(part => part.trim());
      
      if (newTextParts.length === 0) {
        newTextParts.push(newText.trim());
      }
      
      // Replace text nodes intelligently
      if (textNodes.length === 1) {
        // Single text node - replace with all new text
        textNodes[0].textContent = newText;
      } else if (newTextParts.length === 1) {
        // Multiple text nodes but single new text - replace first node, clear others
        textNodes[0].textContent = newText;
        for (let i = 1; i < textNodes.length; i++) {
          textNodes[i].textContent = '';
        }
      } else {
        // Distribute new text parts across text nodes
        const partsPerNode = Math.ceil(newTextParts.length / textNodes.length);
        let partIndex = 0;
        
        textNodes.forEach((textNode, nodeIndex) => {
          const partsForThisNode = [];
          const startIndex = partIndex;
          const endIndex = Math.min(partIndex + partsPerNode, newTextParts.length);
          
          for (let i = startIndex; i < endIndex; i++) {
            if (newTextParts[i]) {
              partsForThisNode.push(newTextParts[i]);
            }
          }
          
          if (partsForThisNode.length > 0) {
            textNode.textContent = partsForThisNode.join('\n\n');
            partIndex = endIndex;
          } else {
            textNode.textContent = '';
          }
        });
      }
      
      return tempDiv.innerHTML;
    } catch (error) {
      console.error('Error replacing text in HTML:', error);
      // Fallback: wrap new text in a simple paragraph
      return `<p>${newText.replace(/\n/g, '<br>')}</p>`;
    }
  };

  // Update editedContent when htmlContent prop changes
  React.useEffect(() => {
    setEditedContent(htmlContent || '');
    setTextOnlyContent(extractTextFromHtml(htmlContent || ''));
  }, [htmlContent]);

  const handleEdit = (mode) => {
    setIsEditing(true);
    setEditMode(mode);
    if (mode === 'textOnly') {
      setTextOnlyContent(extractTextFromHtml(htmlContent || ''));
    } else {
      setEditedContent(htmlContent || '');
    }
  };

  const handleSave = () => {
    if (onContentChange) {
      if (editMode === 'textOnly') {
        // Replace text in HTML structure
        const updatedHtml = replaceTextInHtml(htmlContent, textOnlyContent);
        onContentChange(updatedHtml);
      } else {
        // Save full HTML
        onContentChange(editedContent);
      }
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedContent(htmlContent || '');
    setTextOnlyContent(extractTextFromHtml(htmlContent || ''));
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div 
        className="blog-html-content-editor"
        style={{
          padding: '20px',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h5 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>
              {editMode === 'textOnly' ? 'Edit Text Content' : 'Edit HTML Content'}
            </h5>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                className="btn btn-sm"
                onClick={() => {
                  setEditMode('textOnly');
                  setTextOnlyContent(extractTextFromHtml(htmlContent || ''));
                }}
                style={{
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  backgroundColor: editMode === 'textOnly' ? '#E36250' : '#f3f4f6',
                  color: editMode === 'textOnly' ? 'white' : '#374151',
                  border: editMode === 'textOnly' ? '1px solid #E36250' : '1px solid #d1d5db',
                  cursor: 'pointer'
                }}
              >
                <BiText size={14} /> Text Only
              </button>
              <button
                className="btn btn-sm"
                onClick={() => {
                  setEditMode('fullHtml');
                  setEditedContent(htmlContent || '');
                }}
                style={{
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  backgroundColor: editMode === 'fullHtml' ? '#E36250' : '#f3f4f6',
                  color: editMode === 'fullHtml' ? 'white' : '#374151',
                  border: editMode === 'fullHtml' ? '1px solid #E36250' : '1px solid #d1d5db',
                  cursor: 'pointer'
                }}
              >
                <BiEdit size={14} /> Full HTML
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              style={{
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: '#E36250',
                border: 'none',
                color: 'white'
              }}
            >
              <BiSave size={16} /> Save
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={handleCancel}
              style={{
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <BiX size={16} /> Cancel
            </button>
          </div>
        </div>
        {editMode === 'textOnly' ? (
          <>
            <textarea
              value={textOnlyContent}
              onChange={(e) => setTextOnlyContent(e.target.value)}
              style={{
                width: '100%',
                minHeight: '400px',
                padding: '16px',
                fontFamily: 'inherit',
                fontSize: '14px',
                lineHeight: '1.6',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                resize: 'vertical',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}
              placeholder="Edit text content here (HTML tags are hidden)..."
            />
            <div style={{ 
              marginTop: '12px', 
              padding: '12px', 
              backgroundColor: '#dbeafe', 
              borderRadius: '6px', 
              border: '1px solid #93c5fd',
              fontSize: '12px',
              color: '#1e40af'
            }}>
              <strong>Text Only Mode:</strong> You are editing only the text content. HTML structure and formatting will be preserved.
            </div>
          </>
        ) : (
          <>
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              style={{
                width: '100%',
                minHeight: '400px',
                padding: '16px',
                fontFamily: 'monospace',
                fontSize: '13px',
                lineHeight: '1.6',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                resize: 'vertical',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}
              placeholder="Enter HTML content here..."
            />
            <div style={{ 
              marginTop: '12px', 
              padding: '12px', 
              backgroundColor: '#fef3c7', 
              borderRadius: '6px', 
              border: '1px solid #fde68a',
              fontSize: '12px',
              color: '#92400e'
            }}>
              <strong>Full HTML Mode:</strong> You can edit the complete HTML content directly. Make sure the HTML is valid.
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div 
      className="blog-html-content-wrapper"
      style={{
        padding: '20px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        position: 'relative'
      }}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: '8px',
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <button
          className="btn btn-outline-secondary"
          onClick={() => handleEdit('textOnly')}
          style={{
            borderRadius: '6px',
            padding: '6px 12px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <BiText size={16} /> Edit Text
        </button>
        <button
          className="btn btn-outline-secondary"
          onClick={() => handleEdit('fullHtml')}
          style={{
            borderRadius: '6px',
            padding: '6px 12px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <BiEdit size={16} /> Edit HTML
        </button>
      </div>
      <div 
        className="blog-html-content"
        dangerouslySetInnerHTML={{ __html: htmlContent || '' }}
        style={{
          minHeight: '200px'
        }}
      />
    </div>
  );
};

export default HtmlContentRenderer;
